import { Box, Rhythm } from '@trmediaab/zebra';
import { formatNumber } from '@trmediaab/zebra-utils';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import Article from '~/components/Article';
import { FactHeading, FactItems, FactItemsGroup } from '~/components/Elements';
import Spinner from '~/components/Spinner';
import Time from '~/components/Time';
import Paywall from '~/containers/Auth/Paywall';
import { selectPremium } from '~/containers/Auth/selectors';
import { selectContent, selectLoading } from '~/containers/Content/selectors';
import { createTeaser } from '~/containers/Frontpage/TeaserList';
import PreliminaryEquipments from '~/containers/PreliminaryEquipments';

import Bankers from '../Bankers';
import PreliminaryEquipmentsList from '../PreliminaryEquipmentsList';

function RoundOverview({
  round,
  hasPremium,
  routeType,
  content,
  isContentLoading,
}) {
  const times = [
    {
      label: `Start ${round.bet_type}`,
      value: <Time date={new Date(round.start_time)} format="HH:mm" />,
    },
  ];

  round.meets.forEach(meet => {
    times.unshift({
      label: `Första start${round.meets.length > 1 ? ` ${meet.track_name}` : ''}`,
      value: <Time date={new Date(meet.first_race)} format="HH:mm" />,
    });
  });

  return (
    <Fragment>
      {round.analysis && (
        <Box bg="white" px={{ _: 3, md: '3,5' }} py={{ _: 3, md: '3,5' }}>
          <Article
            type={routeType}
            premiumLocked={!hasPremium}
            article={round.analysis}
          />
        </Box>
      )}

      {hasPremium ? (
        <Fragment>
          <Rhythm between="3" mt="3">
            <Box bg="white" px={{ _: 3, md: '3,5' }} py={{ _: 3, md: '3,5' }}>
              <FactItemsGroup>
                <FactHeading size="24">Fakta</FactHeading>

                <FactItems label="Hålltider" rows={times} highlightValues />

                {['SE', 'DK'].includes(round.meets[0].country) &&
                  round.meets.map(meet => (
                    <FactItems
                      key={meet.id}
                      label={`Om ${meet.track_name}`}
                      rows={[
                        {
                          label: `Banlängd`,
                          value: `${formatNumber(meet.length_total)} m`,
                        },
                        {
                          label: `Banbredd (publiksida)`,
                          value: `${formatNumber(meet.width_1 / 100)} m`,
                        },
                        {
                          label: `Banbredd (bortre långsida)`,
                          value: `${formatNumber(meet.width_2 / 100)} m`,
                        },
                        {
                          label: `Upploppets längd`,
                          value: `${formatNumber(meet.length_home_stretch)} m`,
                        },
                        {
                          label: `Vinklad startbilsvinge`,
                          value: meet.angled_wing ? 'Ja' : 'Nej',
                        },
                      ]}
                      highlightValues
                    />
                  ))}
              </FactItemsGroup>
            </Box>

            <Bankers round={round} />

            <PreliminaryEquipments
              round={round.slug}
              render={preliminaryEquipments =>
                isEmpty(preliminaryEquipments) ? null : (
                  <Box
                    bg="white"
                    px={{ _: 3, md: '3,5' }}
                    py={{ _: 3, md: '3,5' }}
                  >
                    <FactItemsGroup>
                      <PreliminaryEquipmentsList
                        preliminaryEquipments={preliminaryEquipments}
                        bet_type={round.bet_type}
                        legs={round.legs}
                      />
                    </FactItemsGroup>
                  </Box>
                )
              }
            />
          </Rhythm>
        </Fragment>
      ) : (
        <Box bg="white" px={{ _: 3, md: '3,5' }} py={{ _: 3, md: '3,5' }}>
          <Paywall fade={round.analysis != null} />
        </Box>
      )}

      <Spinner isLoading={isContentLoading}>
        <Rhythm between="1" mt="3,5">
          {content &&
            content.results
              .filter(article => article.type !== 'round_analysis')
              .map((data, teaserIndex) =>
                createTeaser(data, teaserIndex, data.round),
              )}
        </Rhythm>
      </Spinner>
    </Fragment>
  );
}

const mapStateToProps = createStructuredSelector({
  hasPremium: selectPremium,
  content: selectContent,
  isContentLoading: selectLoading,
});

RoundOverview.propTypes = {
  round: PropTypes.object.isRequired,
  hasPremium: PropTypes.bool,
  routeType: PropTypes.string.isRequired,
  content: PropTypes.object,
  isContentLoading: PropTypes.bool,
};

export default connect(mapStateToProps)(RoundOverview);
